import { useSelector } from 'react-redux'
import props from '../../../../../../redux/props'
import useConceptConfiguration from '../../ConceptCalculation/useConceptConfiguration'
import { Concept, FloorMappingEntryType } from '../../../../../../types/Survey/Analysis/Concept'
import BarStacked, { Item as BarStackedItem } from '../../../../../UI/Chart/BarStacked/BarStacked'
import { ProductionDistance } from '../../../../../../types/OrganizationUnit'
import { isUnitAllowedByProductionDistance } from '../SummaryProductionDistance'
import { FloorListItem, FloorListItemUnit } from './FloorAnalysis'

type Props = {
  concept: Concept
  productionDistance?: ProductionDistance
}

const FloorComparison: React.FC<Props> = ({ concept, productionDistance }) => {
  const { context } = useConceptConfiguration()
  const t = useSelector((s) => s[props.TRANSLATION])

  const getUnitName = (unitUUID: string) => {
    if (!!concept.units[unitUUID]) {
      return concept.units[unitUUID].name
    }
    return ''
  }

  const getUebergeordneterBedarfName = (bedarfUUID: string) => {
    const bedarf = concept.optimization?.UEBERGEORDNETE_BEDARFE.find((bedarf) => bedarf.uuid === bedarfUUID)
    if (bedarf) {
      return bedarf.name
    }
    return ''
  }

  const getUnitTotalSquareMeter = (unitUUID: string) => {
    if (!!concept.units[unitUUID]) {
      return concept.units[unitUUID].ergebnisse.total
    }
    return 0
  }

  const getUebergeordneterBedarfTotalSquareMeter = (bedarfUUID: string) => {
    const bedarf = concept.optimization?.UEBERGEORDNETE_BEDARFE.find((bedarf) => bedarf.uuid === bedarfUUID)
    if (bedarf) {
      return bedarf.practicalQM
    }
    return 0
  }

  const getFloorList = (): FloorListItem[] => {
    const floors: FloorListItem[] = []
    if (!Array.isArray(context.floors)) return []
    for (const floor of context.floors) {
      if (!concept.floorMapping) {
        floors.push({
          floor,
          items: [],
        })
      } else {
        const items = concept.floorMapping.reduce((acc, mapping) => {
          if (mapping.floorUUID === floor.uuid) {
            if (mapping.type === FloorMappingEntryType.UNIT) {
              acc.push({
                type: mapping.type,
                uuid: mapping.targetUUID,
                name: getUnitName(mapping.targetUUID),
                totalSquareMeter: getUnitTotalSquareMeter(mapping.targetUUID),
              })
            }
            if (mapping.type === FloorMappingEntryType.UEBERGEORDNETER_BEDARF) {
              acc.push({
                type: mapping.type,
                uuid: mapping.targetUUID,
                name: getUebergeordneterBedarfName(mapping.targetUUID),
                totalSquareMeter: getUebergeordneterBedarfTotalSquareMeter(mapping.targetUUID),
              })
            }
          }
          return acc
        }, [] as FloorListItemUnit[])
        floors.push({
          floor,
          items,
        })
      }
    }
    if (productionDistance) {
      return floors.filter((floor) => !!floor.items.find((item) => isUnitAllowedByProductionDistance(concept, item.uuid, productionDistance)))
    }
    return floors
  }

  const getGraphItems = (): BarStackedItem[] => {
    const floorList = getFloorList()
    const allUnitsAndBedarfe = floorList.flatMap((floor) => floor.items)
    return floorList.map((floor) => ({
      key: floor.floor.uuid,
      label: floor.floor.name,
      limit: floor.floor.squareMeter,
      values:
        allUnitsAndBedarfe.length > 0
          ? allUnitsAndBedarfe.map((item) => {
              const hasItem = floor.items.find((i) => i.uuid === item.uuid)
              if (hasItem) {
                return {
                  label: item.name,
                  value: item.totalSquareMeter,
                }
              }
              return {
                label: item.name,
                value: 0,
              }
            })
          : [
              {
                label: 'Keine Daten vorhanden',
                value: 0,
              },
            ],
    }))
  }

  if (!context) return null
  return (
    <div>
      <BarStacked
        showLegend
        useTransparentColors={false}
        items={getGraphItems()}
        suffix={t.generic.squareMeterSuffix}
        showGrid={true}
        enableDownload
      />
    </div>
  )
}

export default FloorComparison
